// resolveTheme.ts — converts a Tweaks snapshot into fully resolved ThemeTokens.
//
// Called once per render in TemplatePage (memoised on tweaks + device).
// All section components receive the resolved ThemeTokens (T) and never
// read from Tweaks directly.

import type { CSSProperties } from "react";
import type { CustomThemeColors, Palette, Tweaks, ThemeTokens } from "./types";
import type { DesignTemplate } from "../../../constants/designTemplates";
import { COLOR_OPTIONS } from "../../../constants/designTemplates";
import {
  DESIGN_CUSTOM_FONT_PAIR_KEY,
  FINDER_FONT_FAMILY,
  FONT_PAIR_OPTIONS,
  FUTURA_FONT_FAMILY,
  resolveFontPairLabel,
  resolveFontPairRoles,
} from "../../../constants/designFonts";
import { PALETTE_NAME_ALIASES } from "../../../constants/designTemplates";
import { PALETTES, FONT_PAIRS, AESTHETICS, AXIS_VALUES } from "./templateData";
import {
  isHexDark,
  normalizeHexColor,
  getContrastRatio,
} from "../../../Utils/HelperFunction/HelperFunction";

const CUSTOM_COLOR_FALLBACKS: CustomThemeColors = {
  primary: "#FFFFFF",
  secondary: "#000000",
  tertiary: "#000000",
  forth: "#e6e7eb",
};

function hexOr(fallback: string, value?: string | null): string {
  return normalizeHexColor(value || "") || normalizeHexColor(fallback) || fallback;
}

/** Mix two #rrggbb colors; `t` is weight toward `b` (0–1). */
function mixHex(a: string, b: string, t: number): string {
  const na = normalizeHexColor(a);
  const nb = normalizeHexColor(b);
  if (!na || !nb) return a;
  const parse = (h: string) => [
    parseInt(h.slice(1, 3), 16),
    parseInt(h.slice(3, 5), 16),
    parseInt(h.slice(5, 7), 16),
  ];
  const [ar, ag, ab] = parse(na);
  const [br, bg, bb] = parse(nb);
  const w = Math.min(1, Math.max(0, t));
  const ch = (x: number, y: number) =>
    Math.round(x + (y - x) * w)
      .toString(16)
      .padStart(2, "0");
  return `#${ch(ar, br)}${ch(ag, bg)}${ch(ab, bb)}`;
}

/**
 * Keep muted text AA-readable on the page background (≥ 4.5:1).
 * Mixes tertiary toward ink until the ratio clears.
 */
function ensureMutedOnBg(
  muted: string,
  bg: string,
  ink: string,
  minRatio = 4.5,
): string {
  const ratio = getContrastRatio(muted, bg);
  if (ratio != null && ratio >= minRatio) return muted;
  let best = muted;
  for (let t = 0.15; t <= 1; t += 0.15) {
    const next = mixHex(muted, ink, t);
    const r = getContrastRatio(next, bg);
    best = next;
    if (r != null && r >= minRatio) return next;
  }
  return ink;
}

/**
 * Expand Design 4-color slots into Palette / ThemeTokens.
 * Elements Guide:
 *   Primary → page background
 *   Secondary → primary text, buttons, borders
 *   Tertiary → muted / placeholder text
 *   Fourth → surface / fill
 */
export function paletteFromCustomColors(
  colors: CustomThemeColors | null | undefined,
): Palette {
  const primary = hexOr(CUSTOM_COLOR_FALLBACKS.primary, colors?.primary);
  const secondary = hexOr(CUSTOM_COLOR_FALLBACKS.secondary, colors?.secondary);
  const tertiary = hexOr(CUSTOM_COLOR_FALLBACKS.tertiary, colors?.tertiary);
  const forth = hexOr(CUSTOM_COLOR_FALLBACKS.forth, colors?.forth);
  const accentInk = isHexDark(secondary) ? "#ffffff" : "#0c0c10";
  const muted = ensureMutedOnBg(tertiary, primary, secondary);

  return {
    primary: secondary,
    secondary,
    tertiary,
    bg: primary,
    surface: forth,
    ink: secondary,
    headerInk: secondary,
    muted,
    accent: secondary,
    accent2: tertiary,
    accentInk,
  };
}

/** Map a Design tab COLOR_OPTIONS entry into MTE Palette tokens. */
function designTemplateToPalette(template: DesignTemplate): Palette {
  return paletteFromCustomColors({
    primary: template.background,
    secondary: template.button.background,
    tertiary: template.text || template.button.text,
    forth: template.card_style || CUSTOM_COLOR_FALLBACKS.forth,
  });
}

const ACTIVE_COLOR_OPTIONS = COLOR_OPTIONS.filter(
  (o) => o.name && o.name !== "name" && normalizeHexColor(o.background),
);

/** Resolve named Design / Custom / legacy MTE palette from tweaks. */
function resolvePalette(t: Tweaks): Palette {
  if (t.palette === "Custom" && t.customColors) {
    return paletteFromCustomColors(t.customColors);
  }
  const paletteName = PALETTE_NAME_ALIASES[t.palette] || t.palette;
  const designOpt = ACTIVE_COLOR_OPTIONS.find((o) => o.name === paletteName);
  if (designOpt) return designTemplateToPalette(designOpt);
  // Legacy MTE named palettes (Cellar, Midnight, …) for already-published sites.
  if (t.palette && PALETTES[t.palette]) return PALETTES[t.palette];
  const fallback =
    ACTIVE_COLOR_OPTIONS.find((o) => o.name === "Default") ||
    ACTIVE_COLOR_OPTIONS[0];
  return fallback
    ? designTemplateToPalette(fallback)
    : paletteFromCustomColors(CUSTOM_COLOR_FALLBACKS);
}

type ResolvedFontFamilies = {
  display: string;
  body: string;
  uiFont: string;
  mono: string;
  displayTrack: string;
  displayWeight: number;
  displaySizeScale: number;
  navWeight: number;
  categoryWeight: number;
  captionWeight: number;
  typeLabelWeight: number;
};

/** Resolve Design FONT_PAIR_OPTIONS (or Custom primary/secondary) into theme fonts. */
function resolveFontFamilies(t: Tweaks): ResolvedFontFamilies {
  const pairLabel = resolveFontPairLabel(t.fontPair);
  const legacyPair = FONT_PAIRS[t.fontPair] || FONT_PAIRS[pairLabel];
  const designPair = FONT_PAIR_OPTIONS.find((p) => p.label === pairLabel);
  const roles = resolveFontPairRoles(designPair);

  let display = FUTURA_FONT_FAMILY;
  let body = FUTURA_FONT_FAMILY;
  let displayTrack = roles.displayTracking;
  let displayWeight = roles.displayWeight;
  let displaySizeScale = roles.displaySizeScale;
  let navWeight = roles.navWeight;
  let categoryWeight = roles.categoryWeight;
  let captionWeight = roles.captionWeight;
  let typeLabelWeight = roles.typeWeight;

  if (t.fontPair === DESIGN_CUSTOM_FONT_PAIR_KEY) {
    display = t.primaryFont?.trim() || FUTURA_FONT_FAMILY;
    body = t.secondaryFont?.trim() || FUTURA_FONT_FAMILY;
  } else if (designPair) {
    display = designPair.primary;
    body = designPair.secondary;
  } else if (legacyPair) {
    // Old Editorial/Modernist/… names: keep track/weight; families were always Finder.
    display = FINDER_FONT_FAMILY;
    body = FINDER_FONT_FAMILY;
    displayTrack = legacyPair.displayTrack;
    displayWeight = legacyPair.displayWeight;
    displaySizeScale = 1;
    navWeight = 400;
    categoryWeight = 300;
    captionWeight = 400;
    typeLabelWeight = 400;
  }

  return {
    display,
    body,
    uiFont: body,
    mono: FINDER_FONT_FAMILY,
    displayTrack,
    displayWeight,
    displaySizeScale,
    navWeight,
    categoryWeight,
    captionWeight,
    typeLabelWeight,
  };
}

export type TemplateStayEmbedLayout = "carousel" | "grid";

/**
 * Template styling for live `stayuptodate_components` embeds.
 * `carousel` — frontend LiB horizontal carousels (default on link-in-bio).
 * `grid` — shop/content tab multi-column grid (`RenderIndividualUI`).
 */
export type TemplateStayEmbedOptions = {
  device: ThemeTokens["device"];
  accent: string;
  layout?: TemplateStayEmbedLayout;
  /** `--tpl-*` + feed surface vars on the embed root (website template link-in-bio). */
  surfaceStyle?: CSSProperties;
};

/** Tokens passed into LiB feed/search components on the website template only. */
export type TemplateFeedDesignTokens = {
  primaryColor: string;
  secondaryColor: string;
  tertiaryColor: string;
  forthColor: string;
  fontFamily: string;
  primaryFontFamily: string;
  secondaryFontFamily: string;
  templateName: string;
  text_color: string;
};

/**
 * Map resolved template theme → feed/search `designTokens`.
 * Elements Guide: primary=bg, secondary=ink, tertiary=muted, fourth=surface.
 */
export function templateFeedDesignTokens(
  theme: ThemeTokens,
): TemplateFeedDesignTokens {
  return {
    primaryColor: theme.bg,
    secondaryColor: theme.ink,
    tertiaryColor: theme.muted,
    forthColor: theme.surface,
    text_color: theme.ink,
    fontFamily: theme.body,
    primaryFontFamily: theme.display,
    secondaryFontFamily: theme.body,
    templateName: theme.paletteName,
  };
}

/** Props for `DesignStaticProvider` on website-template embeds (not LiB `DesignProvider`). */
export function templateDesignStaticProps(theme: ThemeTokens): {
  fontFamily: string;
  primaryFont: string;
  secondaryFont: string;
  primaryColor: string;
  secondaryColor: string;
  tertiaryColor: string;
  forthColor: string;
  socialIconColor: string;
  templateName: string;
} {
  return {
    fontFamily: theme.body,
    primaryFont: theme.display,
    secondaryFont: theme.body,
    primaryColor: theme.bg,
    secondaryColor: theme.ink,
    tertiaryColor: theme.muted,
    forthColor: theme.surface,
    socialIconColor: isHexDark(theme.bg) ? "#FFFFFF" : "#000000",
    templateName: theme.paletteName,
  };
}

export function resolveTheme(
  t: Tweaks,
  device: "desktop" | "tablet" | "mobile" = "desktop",
): ThemeTokens {
  const p   = resolvePalette(t);
  const fonts = resolveFontFamilies(t);
  const aes = AESTHETICS[t.aesthetic] || AESTHETICS.Editorial;
  const ax  = t.axes || {};

  // Prefer explicit axis override, otherwise inherit from aesthetic preset
  const get = <K extends keyof typeof aes>(k: K) =>
    (ax[k] != null ? ax[k] : aes[k]) as (typeof aes)[K];

  const density  = get("density");
  const dens     = AXIS_VALUES.density[density] || AXIS_VALUES.density.regular;
  const radius   = get("radius");
  const r        = AXIS_VALUES.radius[radius] ?? "0px";
  const borders  = get("borders");
  const b        = AXIS_VALUES.borders[borders] || "transparent";
  const tw       = get("typeWeight");
  const w        = AXIS_VALUES.typeWeight[tw] ?? 400;
  const it       = get("imageTreatment");
  const imgFilter =
    it === "grain"
      ? "sepia(0.18) contrast(1.05)"
      : it === "duotone"
        ? "grayscale(1) contrast(1.15) brightness(0.95)"
        : "none";
  const dropCaps = !!get("dropCaps");

  // Gutter adapts to page container width via cqi so dashboard preview frame
  // and public site both compute the same gutter at the same rendered width.
  const gutter = "clamp(16px, 4cqi, 64px)";

  return {
    ...p,
    muted: ensureMutedOnBg(p.muted, p.bg, p.ink),
    paletteName:
      t.palette === "Custom"
        ? "Custom"
        : PALETTE_NAME_ALIASES[t.palette] || t.palette?.trim() || "Default",
    display: fonts.display,
    body: fonts.body,
    uiFont: fonts.uiFont,
    mono: fonts.mono,
    displayTrack:  fonts.displayTrack,
    displayWeight: fonts.displayWeight || w,
    displaySizeScale: fonts.displaySizeScale,
    navWeight: fonts.navWeight,
    categoryWeight: fonts.categoryWeight,
    captionWeight: fonts.captionWeight,
    typeLabelWeight: fonts.typeLabelWeight,
    sectionPad:    dens.sectionPad,
    stack:         dens.stack,
    gutter,
    radius:        r,
    radiusFull:    "9999px",
    border:        b,
    imgFilter,
    dropCaps,
    device,
  };
}

/** Editor tablet + mobile: compact nav / stacked chrome (matches `getDeviceForTemplateWidth` tablet tier). */
export function narrowTemplateLayout(
  device: "desktop" | "tablet" | "mobile",
): boolean {
  return device === "mobile" || device === "tablet";
}
